<script setup>
import { ref, computed } from 'vue';
import ImageList from '@/components/System/Files/ImageList.vue';
import PdfList from '@/components/System/Files/PdfList.vue';
import ZlButton from '@/components/ZlButton.vue';
import CanvasGlassCard from '@/components/CanvasGlassCard.vue';

// 定义组件别名便于模板使用
const GlassCard = CanvasGlassCard

// --- Mock Data --- (Replace with actual API calls later)
const currentYear = new Date().getFullYear();
const imageYears = ref([ 2024, 2025]);
const pdfYears = ref([2024, 2025]);
const selectedImageType = ref('图片'); // '图片' or 'PDF'
const selectedYear = ref(currentYear);
const selectedMonth = ref(1);

const months = computed(() => {
  // In a real app, fetch available months for the selected year
  return Array.from({ length: 12 }, (_, i) => i + 1);
});

// File fetching and pagination logic moved to child components
const currentPage = ref(1); // Keep for potential parent-level controls if needed, or remove if unused

function selectYear(year, type) {
  selectedImageType.value = type;
  selectedYear.value = year;
  selectedMonth.value = 1; // Reset to first month when year changes
  // currentPage reset is handled within child components now
}

function selectMonth(month) {
  selectedMonth.value = month;
  // currentPage reset is handled within child components now
}

</script>

<template>
  <div class="p20">
    <!-- Left Sidebar -->
    <aside class="sidebar card_section">

      <!-- PDF Section -->
      <section class="file-type-section">
        <div class="type-header">
          <img src="@/assets/icons/wenjianleixing-pdf.png" alt="Image" width="130" height="130" />
        </div>
        <p class="type-description">已完成工单生成的PDF</p>
        <div class="year-list">
          <div v-for="year in imageYears"
               :key="year">
            <ZlButton
                variant="primary" long
                v-if="year === selectedYear&& selectedImageType === 'PDF'"
                @click="selectYear(year, 'PDF')">
              {{year}}
            </ZlButton>
            <ZlButton
                v-else
                long
                @click="selectYear(year, 'PDF')">
              {{year}}
            </ZlButton>
          </div>
        </div>
      </section>
      <!-- Image Section -->
      <GlassCard class="file-type-section">
        <div class="type-header">
            <img src="@/assets/icons/wenjianleixing-tupian.png" alt="Image" width="150" height="130" />
        </div>
        <p class="type-description">用户头像及操作员上传的工单图片</p>
        <div class="year-list">
          <div v-for="year in imageYears"
               :key="year">
            <ZlButton
                variant="primary" long
                v-if="year === selectedYear&& selectedImageType === '图片'"
                @click="selectYear(year, '图片')">
              {{year}}
            </ZlButton>
            <ZlButton
                v-else
                long
                @click="selectYear(year, '图片')">
              {{year}}
            </ZlButton>
          </div>
        </div>
      </GlassCard>

    </aside>

    <!-- Right Content Area -->
    <main class="content-area">
      <!-- Search Bar -->
      <div class="search-bar">
        <a-input-search style="width: 350px;" placeholder="请输入门店名称进行搜索"/>
      </div>

      <!-- Month Tabs & File Display -->
      <nav class="month-tabs">
        <a-tabs :active-key="selectedMonth" @change="selectMonth" lazy-load>
          <a-tab-pane v-for="month in months" :key="month" :title="month +`月`">
            <!-- Conditionally render the correct list component -->
            <ImageList
              v-if="selectedImageType === '图片'"
              :year="selectedYear"
              :month="month"
            />
            <PdfList
              v-else-if="selectedImageType === 'PDF'"
              :year="selectedYear"
              :month="month"
            />
          </a-tab-pane>
        </a-tabs>
      </nav>

      <!-- Pagination is now handled within ImageList and PdfList components -->
      <!-- Remove the old pagination div -->
    </main>
  </div>
</template>

<style scoped>
/* Basic Reset & Layout */

.file-manager-container {
  display: flex;
  font-family: sans-serif;
  height: calc(100vh - 114px); /* Adjust based on your header/nav height */
  overflow: hidden; /* Contain children */
}

/* Sidebar (Left Column) */
.sidebar {
  flex-shrink: 0; /* Prevent sidebar from shrinking */
  width: 200px;
  background-color: var(--color-bg-2);
  border-right: 1px solid var(--color-border-2);
  padding: 15px;
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* Allow scrolling if content overflows */
}

.file-type-section {
  margin-bottom: 20px;
}

.type-header {
  font-weight: bold;
  padding: 10px;
  background-color: var(--color-fill-2);
  border: 1px solid var(--color-border-2);
  border-radius: 4px;
  text-align: center;
  margin-bottom: 10px;
  color: var(--color-text-1);
}

.type-description {
  font-size: 0.9em;
  color: var(--color-text-3);
  margin-bottom: 10px;
  padding: 0 5px;
}

.year-list {
  list-style: none;
}
.year-list div{
  margin: 10px;
}


/* Content Area (Right Column) */
.content-area {
  flex-grow: 1; /* Take remaining space */
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* Allow scrolling */
}

/* Search Bar */
.search-bar {
  margin-bottom: 20px;
  text-align: right;
}
/* Month Tabs */
.month-tabs ul {
  list-style: none;
  display: flex;
  flex-wrap: wrap; /* Allow tabs to wrap */
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-border-2);
}

.month-tabs li {
  padding: 8px 15px;
  border: 1px solid var(--color-border-2);
  border-radius: 4px;
  cursor: pointer;
  background-color: var(--color-bg-1);
  color: var(--color-text-2);
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.month-tabs li:hover {
  background-color: var(--color-fill-2);
}

.month-tabs li.active {
  background-color: var(--color-primary-light-6);
  color: var(--color-white);
  border-color: var(--color-primary-light-6);
}

/* File Display Area */
.file-display {
  flex-grow: 1; /* Take available space before pagination */
  margin-bottom: 20px;
  min-height: 200px; /* Ensure some minimum height */
}

.no-files {
    text-align: center;
    color: var(--color-text-3);
    margin-top: 40px;
    font-size: 1.1em;
}

/* Image Grid */
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); /* Responsive grid */
  gap: 15px;
}

.image-card {
  border: 1px solid var(--color-border-2);
  border-radius: 4px;
  padding: 10px;
  text-align: center;
  background-color: var(--color-bg-1);
  color: var(--color-text-1);
}

.image-placeholder {
  height: 80px; /* Adjust as needed */
  background-color: var(--color-fill-2);
  border: 1px dashed var(--color-border-2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border-radius: 4px;
  color: var(--color-text-3);
}

.image-card span {
  font-size: 0.9em;
  word-break: break-all; /* Prevent long names from overflowing */
}

/* PDF List */
.pdf-list ul {
    list-style: none;
}

.pdf-list li {
    padding: 10px;
    border-bottom: 1px solid var(--color-border-1);
    color: var(--color-text-1);
}

.pdf-list li:last-child {
    border-bottom: none;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: right;
  gap: 5px;
  margin-top: auto; /* Push to bottom */
  padding-top: 15px;
  border-top: 1px solid var(--color-border-1);
}


</style>